// System
import { useMemo } from 'react';
import { parseISO } from 'date-fns';
import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

// Components
import { DateLabel } from '@Components/shared/labels/DateLabel';
import { MinesRoundDto } from '@Modal/reducers/rounds/types';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';
import { useMinesRoundsTable } from '@Modal/hooks/rounds/useMinesRoundsTable';
import { StringTableColumnHeader } from '@Components/shared/tables/columns';
import { DateTableColumnHeader } from '@Components/shared/tables/columns/DateTableColumnHeader/DateTableColumnHeader';

// Features
import TruncateText from '@Components/truncate-text/TruncateText';

// Contracts
import { Filterable } from '@contracts/common/traits/Filterable';
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';

// Utils
import {
  MinesRoundListColumns,
  MinesRoundListFilter,
  minesRoundListColumns,
} from '@utils/tables/rounds/minesRoundListFilter';
import { useDispatch } from 'react-redux';

type HeaderOptions = {
  filter: Filterable<MinesRoundListFilter>;
};

type Columns = TableColumnDescriptionArray<
  MinesRoundDto,
  HeaderOptions,
  typeof minesRoundListColumns
>;

type Column = TableColumnDescription<MinesRoundDto, HeaderOptions, MinesRoundListColumns>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useMinesRoundsTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();
  const dispatch = useDispatch();
  const { handleFilterSelect, setRoundTableFilters } = useMinesRoundsTable();

  return useMemo(
    (): Column[] =>
      [
        {
          name: 'minesRound._id',
          title: 'ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'id'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>{row?._id && <TruncateText text={row?._id} maxLength={8} textStyles={styles.id} />}</>
          ),
        },
        {
          name: 'minesRound.roundId',
          title: 'Round ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'roundId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>
              {row?.roundId && (
                <TruncateText text={row?.roundId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'minesRound.playerId',
          title: 'Player ID',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'playerId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>{row?.playerId && <Typography variant="body2">{row?.playerId}</Typography>}</>
          ),
        },
        {
          name: 'minesRound.createdAt',
          title: 'Created At',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => (
            <DateTableColumnHeader
              title={title}
              onApply={(startDate, endDate) => {
                startDate?.length
                  ? dispatch(
                      setRoundTableFilters({
                        codeName: 'createdStartDate',
                        value: startDate,
                      })
                    )
                  : null;
                endDate?.length
                  ? dispatch(
                      setRoundTableFilters({
                        codeName: 'createdEndDate',
                        value: endDate,
                      })
                    )
                  : null;
              }}
              onReset={() => {
                dispatch(
                  setRoundTableFilters({
                    codeName: 'createdStartDate',
                    value: undefined,
                  })
                );
                dispatch(
                  setRoundTableFilters({
                    codeName: 'createdEndDate',
                    value: undefined,
                  })
                );
              }}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.createdAt && (
                <DateLabel date={new Date(parseISO(row?.createdAt))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'minesRound.roundHash',
          title: 'Round Hash',
          sx: { minWidth: '170px', maxWidth: '240px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'roundHash'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {
                <>
                  {row?.roundHash && (
                    <TruncateText text={row?.roundHash} maxLength={8} textStyles={styles.id} />
                  )}
                </>
              }
            </Box>
          ),
        },
        {
          name: 'minesRound.clientHash',
          title: 'Client Hash',
          sx: { minWidth: '180px', maxWidth: '200px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'clientHash'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {
                <>
                  {row?.clientHash && (
                    <TruncateText text={row?.clientHash} maxLength={8} textStyles={styles.id} />
                  )}
                </>
              }
            </Box>
          ),
        },
        {
          name: 'minesRound.serverHash',
          title: 'Server Hash',
          sx: { minWidth: '180px', maxWidth: '200px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'serverHash'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.serverHash && (
                <TruncateText text={row?.serverHash} maxLength={8} textStyles={styles.id} />
              )}
            </Box>
          ),
        },
      ] satisfies Columns,
    [ellipsis]
  );
};
